// Data access layer for Ace Asia Productions.
//
// - Public catalogue reads (products, multimedia) come from Cloud Firestore.
// - Customer writes (order, contact) and order tracking go through public
//   Cloud Functions under /api.
// - Admin operations go through auth-protected /api/admin/** endpoints; the
//   caller's Firebase ID token (admin claim required) is attached automatically.

import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db, app } from "./firebase";

const auth = getAuth(app);

// Cloud Functions are exposed under /api (Firebase Hosting rewrite).
const API_BASE = process.env.REACT_APP_API_BASE || "/api";

async function apiRequest(path, { method = "GET", body, token } = {}) {
  const headers = {};
  if (body) headers["Content-Type"] = "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    if (res.status === 404) return null;
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Request failed (${res.status})`);
  }
  return res.json();
}

async function adminRequest(path, opts = {}) {
  const user = auth.currentUser;
  if (!user) throw new Error("Not authenticated");
  const token = await user.getIdToken();
  return apiRequest(path, { ...opts, token });
}

// ---------- PRODUCTS (public, Firestore) ----------
function isVisible(p) {
  return p.productStatus !== "draft" && p.productStatus !== "archived";
}

export async function fetchProducts({ category, query, sort } = {}) {
  const snap = await getDocs(collection(db, "products"));
  let list = snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter(isVisible);

  if (category && category !== "all") {
    list = list.filter((p) => p.category === category);
  }
  if (query) {
    const q = query.toLowerCase();
    list = list.filter(
      (p) =>
        (p.title || "").toLowerCase().includes(q) ||
        (p.shortDescription || "").toLowerCase().includes(q),
    );
  }
  if (sort === "price_asc") list.sort((a, b) => a.price - b.price);
  if (sort === "price_desc") list.sort((a, b) => b.price - a.price);
  if (sort === "title") list.sort((a, b) => a.title.localeCompare(b.title));
  return list;
}

export async function fetchProductById(id) {
  const d = await getDoc(doc(db, "products", id));
  if (!d.exists()) return null;
  return { id: d.id, ...d.data() };
}

// ---------- MULTIMEDIA (public, Firestore) ----------
export async function fetchMultimedia() {
  const snap = await getDocs(collection(db, "multimedia"));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((m) => m.status !== "draft")
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
}

// Admin view: includes drafts (multimedia is publicly readable per rules).
export async function fetchMultimediaAll() {
  const snap = await getDocs(collection(db, "multimedia"));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
}

// ---------- CATEGORIES ----------
export async function fetchCategories() {
  const snap = await getDocs(collection(db, "categories"));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
}

export async function createCategory(payload) {
  return adminRequest("/admin/categories", { method: "POST", body: payload });
}

export async function updateCategory(id, payload) {
  return adminRequest(`/admin/categories/${encodeURIComponent(id)}`, { method: "PATCH", body: payload });
}

export async function deleteCategory(id) {
  return adminRequest(`/admin/categories/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// ---------- PRICING RULES ----------
// Shipping zones, free-delivery thresholds and bulk discounts. Read through the
// API rather than Firestore directly, so the storefront and the server are
// always looking at the same shape. Checkout re-prices server-side regardless.
export async function fetchPricing() {
  return apiRequest("/pricing");
}

export async function fetchPricingAdmin() {
  return adminRequest("/admin/settings/pricing");
}

export async function savePricing(payload) {
  return adminRequest("/admin/settings/pricing", { method: "PUT", body: payload });
}

// ---------- ORDERS (customer, Cloud Functions) ----------
export async function createOrder(payload) {
  return apiRequest("/checkout", { method: "POST", body: payload });
}

export async function fetchOrderByLookup({ orderId, email }) {
  const params = new URLSearchParams();
  if (orderId) params.set("orderId", orderId);
  if (email) params.set("email", email);
  return apiRequest(`/order-status?${params.toString()}`);
}

// ---------- CONTACT (customer, Cloud Functions) ----------
export async function submitContact(payload) {
  return apiRequest("/contact", { method: "POST", body: payload });
}

// ======================= ADMIN (auth-protected) =======================

export async function fetchOrders() {
  return (await adminRequest("/admin/orders")) || [];
}

export async function updateOrderStatus(orderId, patch) {
  return adminRequest(`/admin/orders/${encodeURIComponent(orderId)}`, {
    method: "PATCH",
    body: patch,
  });
}

// Permanent — used to clear test orders. Real orders are financial records,
// so the admin UI confirms each one individually.
export async function deleteOrder(orderId) {
  return adminRequest(`/admin/orders/${encodeURIComponent(orderId)}`, {
    method: "DELETE",
  });
}

export async function fetchContacts() {
  return (await adminRequest("/admin/contacts")) || [];
}

export async function updateContactStatus(id, status) {
  return adminRequest(`/admin/contacts/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: { status },
  });
}

export async function deleteContact(id) {
  return adminRequest(`/admin/contacts/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function createProduct(payload) {
  return adminRequest("/admin/products", { method: "POST", body: payload });
}

export async function updateProduct(id, payload) {
  return adminRequest(`/admin/products/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: payload,
  });
}

export async function deleteProduct(id) {
  return adminRequest(`/admin/products/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function addMultimedia(payload) {
  return adminRequest("/admin/multimedia", { method: "POST", body: payload });
}

// Upload an image file; returns { url }. Reads the file as a base64 data URL
// and posts it to the admin upload endpoint (stored in Firebase Storage).
function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export async function uploadImage(file, folder = "multimedia") {
  const dataUrl = await readAsDataUrl(file);
  const res = await adminRequest("/admin/upload", {
    method: "POST",
    body: { dataUrl, filename: file.name, folder },
  });
  return res.url;
}

export async function updateMultimedia(id, payload) {
  return adminRequest(`/admin/multimedia/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: payload,
  });
}

export async function deleteMultimedia(id) {
  return adminRequest(`/admin/multimedia/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}
